import type { ReactElement } from "react";
import { GlobalCommandPalette, type SurfaceCommand } from "./GlobalCommandPalette.tsx";
import type {
  ReviewCommandDefinition,
  ReviewCommandGroup,
  ReviewCommandId
} from "./review-command-model";

export interface ReviewCommandItem {
  readonly definition: ReviewCommandDefinition & { readonly id: ReviewCommandId };
  readonly enabled: boolean;
  readonly unavailableReason?: string;
  readonly run: () => void;
  readonly runAfterClose?: boolean;
}

interface ReviewCommandPaletteProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly commands: readonly ReviewCommandItem[];
  readonly trigger: ReactElement;
}

const COMMAND_GROUPS: readonly ReviewCommandGroup[] = [
  "Sections",
  "Navigation",
  "Review"
];

/** Shape review commands for the shared palette, preserving their semantics. */
export function toSurfaceCommands(
  commands: readonly ReviewCommandItem[]
): readonly SurfaceCommand[] {
  return commands.map((command) => ({
    id: command.definition.id,
    label: command.definition.label,
    group: command.definition.group,
    keywords: command.definition.keywords,
    shortcutId: command.definition.id,
    shortcut: command.definition.shortcut,
    run: command.run,
    enabled: command.enabled,
    ...(command.unavailableReason
      ? { unavailableReason: command.unavailableReason }
      : {}),
    ...(command.runAfterClose ? { runAfterClose: true } : {})
  }));
}

/**
 * The review workbench's own palette. It stays a controlled instance so it does
 * not bind the global chord; the same commands also reach the global palette
 * through the surface registry.
 */
export function ReviewCommandPalette({
  open,
  onOpenChange,
  commands,
  trigger
}: ReviewCommandPaletteProps) {
  return (
    <GlobalCommandPalette
      open={open}
      onOpenChange={onOpenChange}
      extraCommands={toSurfaceCommands(commands)}
      groupOrder={COMMAND_GROUPS}
      trigger={trigger}
      title="Review commands"
      description="Search actions or use the documented shortcuts while focus is in the Review workbench. Editable fields and Monaco keep their keys."
      closeLabel="Close review commands"
      searchLabel="Search review commands"
      searchPlaceholder="Search review commands…"
      listLabel="Available review commands"
      emptyLabel="No matching review command."
    />
  );
}
